<template>
  <div class="submission-steps-wrap w-full mb-6 mt-16 text-center">
    <div 
      class="submission-steps inline-block mx-auto relative"
      :class="{ 'reverse': isReverse }"
      :data-count="steps.length"
    >
      <!-- Progress line background -->
      <div class="absolute top-0 left-0 w-full h-1 bg-gray-300"></div>
      
      <!-- Step items -->
      <a
        v-for="(step, index) in steps"
        :key="step.key"
        :href="step.link || '#'"
        :class="[
          'step',
          'inline-block relative pt-3 px-9 text-gray-600 text-sm font-bold tracking-tight cursor-pointer',
          step.status,
          { 'first': index === 0, 'last': index === steps.length - 1 }
        ]"
        :data-index="index + 1"
        @click="handleStepClick(step, index, $event)"
      >
        {{ step.name }}
      </a>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SubmissionSteps',
  props: {
    currentState: {
      type: String,
      required: true
    },
    submissionId: {
      type: [String, Number],
      default: null
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isReverse: false,
      // Map workflow states to user-friendly step names
      // Updated to match current two-level state machine architecture
      stateMapping: {
        // Submission-level states
        'App\\States\\Submitted': { name: 'Submission Received', order: 1 },
        'App\\States\\Received': { name: 'Processing Started', order: 2 },
        'App\\States\\Completed': { name: 'Work Completed', order: 9 },
        'App\\States\\Shipped': { name: 'Shipped to Customer', order: 10 },
        'App\\States\\Cancelled': { name: 'Cancelled', order: 11 },
        
        // Card-level states (for individual card tracking)
        'App\\States\\CardAssessment': { name: 'Damage Assessment', order: 3 },
        'App\\States\\CardInProgress': { name: 'Work in Progress', order: 6 },
        'App\\States\\CardQualityCheck': { name: 'Quality Review', order: 7 },
        'App\\States\\CardLabelSlab': { name: 'Label & Slab', order: 8 },
        'App\\States\\CardCompleted': { name: 'Card Completed', order: 9 },
        'App\\States\\CardCancelled': { name: 'Card Cancelled', order: 11 }
      }
    }
  },
  computed: {
    steps() {
      const currentOrder = this.stateMapping[this.currentState]?.order || 1;
      
      // Create steps array with status based on current state
      return Object.entries(this.stateMapping)
        .filter(([key]) => key !== 'cancelled') // Exclude cancelled from normal flow
        .sort(([,a], [,b]) => a.order - b.order)
        .map(([key, config], index) => {
          let status = null;
          
          if (config.order < currentOrder) {
            status = 'done';
          } else if (config.order === currentOrder) {
            status = 'active';
          }
          
          return {
            key,
            name: config.name,
            status,
            order: config.order,
            link: this.readonly ? null : this.getStepLink(key)
          };
        });
    }
  },
  methods: {
    handleStepClick(step, index, event) {
      if (this.readonly) {
        event.preventDefault();
        return;
      }
      
      // Determine animation direction
      const activeIndex = this.steps.findIndex(s => s.status === 'active');
      
      if (index < activeIndex) {
        this.isReverse = true;
      } else {
        this.isReverse = false;
      }
      
      // Emit event for parent component to handle state transition
      this.$emit('step-clicked', {
        step: step.key,
        direction: index < activeIndex ? 'backward' : 'forward'
      });
      
      event.preventDefault();
    },
    
    getStepLink(stateKey) {
      if (!this.submissionId) return null;
      return `/nova/resources/submissions/${this.submissionId}/transition/${stateKey}`;
    }
  },
  watch: {
    currentState(newState, oldState) {
      // Update animation direction when state changes externally
      const newOrder = this.stateMapping[newState]?.order || 1;
      const oldOrder = this.stateMapping[oldState]?.order || 1;
      
      this.isReverse = newOrder < oldOrder;
    }
  }
}
</script>

<style scoped>
/* Base step styling */
.submission-steps {
  position: relative;
}

.submission-steps::before {
  content: '';
  display: block;
  position: relative;
  top: 3px;
  left: 0;
  width: 100%;
  height: 3px;
  background: #bfbfbf;
  z-index: 1;
}

.step {
  position: relative;
  transition: color 0.2s ease-in;
  transition-delay: 0.2s;
}

.step::before {
  content: '';
  display: block;
  position: absolute;
  top: -12px;
  left: -36px;
  width: 0;
  height: 3px;
  background: #4d7298;
  z-index: 2;
  transition: width 0.2s linear;
  transition-delay: 0.2s;
}

.step::after {
  content: attr(data-index);
  display: block;
  position: absolute;
  top: -18px;
  left: calc(50% - 12px);
  width: 24px;
  height: 18px;
  opacity: 0;
  z-index: 3;
  padding: 3px;
  border-radius: 50%;
  background: #4d7298;
  color: #fefefe;
  font-size: 0.875rem;
  font-weight: bold;
  line-height: 18px;
  text-align: center;
  transition: opacity 0.1s ease-in;
  transition-delay: 0.2s;
}

/* First and last step adjustments */
.step.first {
  padding-left: 18px;
}

.step.first::before {
  left: -18px;
}

.step.first::after {
  left: calc(50% - 24px);
}

.step.last::after {
  left: 50%;
}

/* Active step styling */
.step.active {
  color: #0a0a0a;
}

.step.active::before {
  width: calc(50% + 36px);
}

.step.active::after {
  opacity: 1;
}

.step.active.first::before {
  width: calc(50% + 18px);
}

.step.active.last::before {
  width: calc(50% + 36px);
}

/* Done step styling */
.step.done {
  color: #bfbfbf;
}

.step.done::before {
  width: calc(100% + 72px);
}

.step.done.first::before,
.step.done.last::before {
  width: calc(100% + 54px);
}

/* Animation timing for different step counts */
.submission-steps[data-count="10"] .step:nth-child(1)::before,
.submission-steps[data-count="10"] .step:nth-child(1)::after,
.submission-steps[data-count="10"] .step:nth-child(1) {
  transition-delay: calc(0.2s * (10 - 1));
}

.submission-steps[data-count="10"] .step:nth-child(2)::before,
.submission-steps[data-count="10"] .step:nth-child(2)::after,
.submission-steps[data-count="10"] .step:nth-child(2) {
  transition-delay: calc(0.2s * (10 - 2));
}

.submission-steps[data-count="10"] .step:nth-child(3)::before,
.submission-steps[data-count="10"] .step:nth-child(3)::after,
.submission-steps[data-count="10"] .step:nth-child(3) {
  transition-delay: calc(0.2s * (10 - 3));
}

.submission-steps[data-count="10"] .step:nth-child(4)::before,
.submission-steps[data-count="10"] .step:nth-child(4)::after,
.submission-steps[data-count="10"] .step:nth-child(4) {
  transition-delay: calc(0.2s * (10 - 4));
}

.submission-steps[data-count="10"] .step:nth-child(5)::before,
.submission-steps[data-count="10"] .step:nth-child(5)::after,
.submission-steps[data-count="10"] .step:nth-child(5) {
  transition-delay: calc(0.2s * (10 - 5));
}

.submission-steps[data-count="10"] .step:nth-child(6)::before,
.submission-steps[data-count="10"] .step:nth-child(6)::after,
.submission-steps[data-count="10"] .step:nth-child(6) {
  transition-delay: calc(0.2s * (10 - 6));
}

.submission-steps[data-count="10"] .step:nth-child(7)::before,
.submission-steps[data-count="10"] .step:nth-child(7)::after,
.submission-steps[data-count="10"] .step:nth-child(7) {
  transition-delay: calc(0.2s * (10 - 7));
}

.submission-steps[data-count="10"] .step:nth-child(8)::before,
.submission-steps[data-count="10"] .step:nth-child(8)::after,
.submission-steps[data-count="10"] .step:nth-child(8) {
  transition-delay: calc(0.2s * (10 - 8));
}

.submission-steps[data-count="10"] .step:nth-child(9)::before,
.submission-steps[data-count="10"] .step:nth-child(9)::after,
.submission-steps[data-count="10"] .step:nth-child(9) {
  transition-delay: calc(0.2s * (10 - 9));
}

.submission-steps[data-count="10"] .step:nth-child(10)::before,
.submission-steps[data-count="10"] .step:nth-child(10)::after,
.submission-steps[data-count="10"] .step:nth-child(10) {
  transition-delay: calc(0.2s * (10 - 10));
}

/* Reverse animation timing */
.submission-steps.reverse[data-count="10"] .step:nth-child(1)::before,
.submission-steps.reverse[data-count="10"] .step:nth-child(1)::after,
.submission-steps.reverse[data-count="10"] .step:nth-child(1) {
  transition-delay: calc(0.2s * (10 - 1));
}

/* Active and done states override delays */
.submission-steps .step.done::before,
.submission-steps .step.done::after,
.submission-steps .step.done {
  transition-delay: calc(0.2s * (var(--step-index, 1) - 1));
}

.submission-steps .step.active::before,
.submission-steps .step.active::after,
.submission-steps .step.active {
  transition-delay: calc(0.2s * (var(--step-index, 1) - 1));
  transition-timing-function: ease-out;
}
</style>
